"use client";

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import en from "@/lib/locales/en";
import ru from "@/lib/locales/ru";
import cz from "@/lib/locales/cz";

export type Locale = "en" | "ru" | "cz";

/** Locales written right-to-left. None currently — all supported locales are LTR. */
export const RTL_LOCALES: Locale[] = [];

/** Text direction for a locale: "rtl" for RTL locales, "ltr" otherwise. */
export function getLangDir(locale: Locale): "rtl" | "ltr" {
  return RTL_LOCALES.includes(locale) ? "rtl" : "ltr";
}

type Dict = typeof en;

const dictionaries: Record<Locale, Dict> = {
  en,
  ru: ru as unknown as Dict,
  cz: cz as unknown as Dict,
};

interface I18nContextType {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Dict;
}

const I18nContext = createContext<I18nContextType>({
  locale: "en",
  setLocale: () => {},
  t: en,
});

const STORAGE_KEY = "apexprep_lang";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("cz");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && dictionaries[saved]) setLocaleState(saved);
  }, []);

  // Keep <html lang> and text direction in sync with the active UI locale.
  // The server renders lang="en" by default; this updates it on mount and on
  // every locale switch (direction stays LTR for all current locales).
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = getLangDir(locale);
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t: dictionaries[locale] }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
